// layouts/dashboard/components/OrdersOverview.js

import { useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { keyframes } from "@mui/system";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import TimelineItem from "examples/Timeline/TimelineItem";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaymentsIcon from "@mui/icons-material/Payments";

// Premium Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

function OrdersOverview({ payments = [] }) {
  const navigate = useNavigate();
  const cardRef = useRef(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
      case "captured":
        return { bg: "linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)", label: "SUCCESS" };
      case "created":
      case "pending":
        return { bg: "linear-gradient(135deg, #ed6c02 0%, #ff9800 100%)", label: "PENDING" };
      default:
        return { bg: "linear-gradient(135deg, #d32f2f 0%, #f44336 100%)", label: "FAILED" };
    }
  };

  return (
    <Card
      ref={cardRef}
      sx={{
        height: "100%",
        borderRadius: "24px",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        "&:hover": {
          transform: "translateY(-8px) scale(1.02)",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #2196f3, #4caf50, #ff9800, #f44336, #2196f3)",
          animation: `${shimmer} 3s infinite linear`,
          backgroundSize: "200% 100%",
        },
      }}
    >
      {/* Animated background gradient */}
      <MDBox
        sx={{
          position: "absolute",
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(33,150,243,0.15) 0%, rgba(33,150,243,0) 70%)",
          pointerEvents: "none",
          animation: `${pulseGlow} 3s infinite`,
        }}
      />

      {/* Header with 3D effect */}
      <MDBox pt={3} px={3} sx={{ position: "relative", zIndex: 1 }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDTypography variant="h6" fontWeight="bold" sx={{ letterSpacing: "-0.5px" }}>
            Recent Payments
          </MDTypography>
          <Tooltip title="View all payments">
            <IconButton
              size="small"
              sx={{
                bgcolor: "rgba(33,150,243,0.1)",
                transition: "all 0.3s",
                "&:hover": {
                  bgcolor: "rgba(33,150,243,0.2)",
                  transform: "rotate(90deg)",
                },
              }}
              onClick={() => navigate("/payment")}
            >
              <PaymentsIcon sx={{ color: "#2196f3" }} />
            </IconButton>
          </Tooltip>
        </MDBox>
        <MDBox mt={0} mb={0}>
          <MDTypography variant="button" color="text" fontWeight="regular">
            <MDTypography display="inline" variant="body2" verticalAlign="middle">
              <Icon sx={{ color: "#4caf50" }}>payments</Icon>
            </MDTypography>
            &nbsp;
            <MDTypography variant="button" color="text" fontWeight="bold">
              {payments.length}
            </MDTypography>{" "}
            recent transactions
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox p={2} sx={{ position: "relative", zIndex: 1 }}>
        {payments.length === 0 ? (
          <MDTypography
            variant="body2"
            color="textSecondary"
            textAlign="center"
            sx={{ py: 4, animation: `${fadeInUp} 0.5s ease-out` }}
          >
            No payments yet
          </MDTypography>
        ) : (
          payments.map((payment, index) => {
            const statusInfo = getStatusColor(payment.status);
            return (
              <MDBox
                key={payment._id}
                sx={{
                  animation: `${slideInRight} 0.4s ease-out ${index * 0.1}s both`,
                  "&:hover": {
                    transform: "translateX(8px)",
                    transition: "transform 0.2s ease",
                  },
                }}
              >
                <TimelineItem
                  color={
                    statusInfo.label === "SUCCESS"
                      ? "success"
                      : statusInfo.label === "PENDING"
                      ? "warning"
                      : "error"
                  }
                  icon={
                    statusInfo.label === "SUCCESS"
                      ? "check_circle"
                      : statusInfo.label === "PENDING"
                      ? "pending"
                      : "cancel"
                  }
                  title={
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDTypography variant="body2" fontWeight="bold">
                        {payment.user?.name}
                      </MDTypography>
                      {/* <Tooltip title="View Payment">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/payment-details/${payment._id}`)}
                          sx={{
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "scale(1.2)",
                              color: "#2196f3",
                            },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip> */}
                    </MDBox>
                  }
                  dateTime={
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      {formatDate(payment.createdAt)}
                    </MDTypography>
                  }
                  description={
                    <MDBox mt={1} display="flex" flexWrap="wrap" gap={1}>
                      <Chip
                        label={`₹${payment.amount}`}
                        size="small"
                        sx={{
                          background: "linear-gradient(135deg, #f44336 0%, #ef5350 100%)",
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                          boxShadow: "0 2px 5px rgba(244,67,54,0.3)",
                          transition: "all 0.2s",
                          "&:hover": {
                            transform: "scale(1.05)",
                            boxShadow: "0 4px 10px rgba(244,67,54,0.5)",
                          },
                        }}
                      />
                      <Chip
                        label={payment.plan?.name}
                        size="small"
                        sx={{
                          background: "linear-gradient(135deg, #1565c0 0%, #2196f3 100%)",
                          color: "#fff",
                          fontWeight: "medium",
                          fontSize: "0.7rem",
                          boxShadow: "0 2px 5px rgba(33,150,243,0.3)",
                        }}
                      />
                      <Chip
                        label={statusInfo.label}
                        size="small"
                        sx={{
                          background: statusInfo.bg,
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "0.7rem",
                          boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                          animation:
                            statusInfo.label === "PENDING" ? `${pulseGlow} 1.5s infinite` : "none",
                        }}
                      />
                    </MDBox>
                  }
                  lastItem={index === payments.length - 1}
                />
              </MDBox>
            );
          })
        )}
      </MDBox>

      {/* 3D decorative element */}
      <MDBox
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: 100,
          height: 100,
          background: "radial-gradient(circle, rgba(33,150,243,0.08) 0%, rgba(33,150,243,0) 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />
    </Card>
  );
}

OrdersOverview.propTypes = {
  payments: PropTypes.array,
};

export default OrdersOverview;
